import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../lib/api';

interface BrandConfig {
    name: string;
    tagline: string;
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
    logo_url?: string;
    bio?: string;
    features?: string[];
}

interface BrandContextType {
    config: BrandConfig;
    loading: boolean;
}

const DEFAULT_BRAND_CONFIG: BrandConfig = {
    name: 'DIYElectronicMech',
    tagline: 'Fix it. Build it. Understand it.',
    primary_color: '#00ff88', // Electric green
    secondary_color: '#00cfff', // Cyan
    accent_color: '#ffaa00', // Amber
    bio: 'Empowering electronics hobbyists, makers, and engineers with AI-powered diagnostics and project guidance.',
    features: ['Diagnose', 'Components', 'Schematics', 'Projects', 'Learn']
};

const BrandContext = createContext<BrandContextType>({ 
    config: DEFAULT_BRAND_CONFIG, 
    loading: true 
});

export const useBrand = () => useContext(BrandContext);

export function BrandProvider({ children }: { children: ReactNode }) {
    const [config, setConfig] = useState<BrandConfig>(DEFAULT_BRAND_CONFIG);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchConfig() {
            try {
                const data = await api.getBrandConfig();
                if (data && data.name) {
                    setConfig(data);
                }
            } catch (error) {
                console.warn('Backend brand config not found, using electronics defaults:', error);
                // We keep DEFAULT_BRAND_CONFIG if fetch fails
            } finally {
                setLoading(false);
            }
        }
        fetchConfig();
    }, []);

    return (
        <BrandContext.Provider value={{ config, loading }}>
            {children}
        </BrandContext.Provider>
    );
}
